import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportsService, clientsService, sitesService, auditorsService, jobsService, settingsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import RegionFilter from '../components/RegionFilter';
import { useSettings } from '../contexts/SettingsContext';
import SiteCustomerCombobox from '../components/SiteCustomerCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  BarChart3, Loader2, CheckCircle, Calendar,
  ChevronRight, Printer, FileSpreadsheet,
  Grid3X3, List, PoundSterling, CalendarRange, ChevronDown, Building2, Award, ClipboardList,
  TrendingUp, Search, Briefcase, EyeOff, Receipt
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, startOfQuarter, endOfQuarter, parseISO } from 'date-fns';

// Extracted components
import JobTrackerReport from '../components/reports/JobTrackerReport';
import ScheduleMatrixReport from '../components/reports/ScheduleMatrixReport';
import UnpublishJobsDialog from '../components/UnpublishJobsDialog';
import RevenueForecastReport from '../components/reports/RevenueForecastReport';
import PreparationQualityReport from '../components/reports/PreparationQualityReport';
import DrilldownDialog from '../components/reports/DrilldownDialog';
import { useSalesInvoicingModuleStatus } from '../hooks/useSalesInvoicingModuleStatus';

// Pure helpers
import {
  getJobCustomer, getJobLabel, getJobSpan,
  getJobsForAuditorDate, getDateRange, getBankHoliday, exportToCSV
} from '../utils/reportHelpers';

export default function ReportsPage() {
  const { user, isAdmin, isScheduler, isAuditor, isSeniorScheduler } = useAuth();
  const { toast } = useToast();
  const { formatCurrency, currencySymbol, forecastAllocationDays, forecastAllocationAuditors, region } = useSettings();
  const navigate = useNavigate();
  const { enabled: salesInvoicingEnabled, loading: salesInvoicingStatusLoading } = useSalesInvoicingModuleStatus();
  const [searchParams] = useSearchParams();
  const [printBlocked, setPrintBlocked] = useState(false);

  // Selected report — can be pre-selected via ?report=<id> query param
  const [selectedReport, setSelectedReport] = useState(() => searchParams.get('report') || null);
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [trackerPage, setTrackerPage] = useState(0);
  const trackerPageSize = 50;

  // Preparation Quality data
  const [qualityData, setQualityData] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);

  // Reference data
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [auditors, setAuditors] = useState([]);

  // Filters - Default to This Month
  const today = new Date();
  const [startDate, setStartDate] = useState(format(startOfMonth(today), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(today), 'yyyy-MM-dd'));
  const [siteCustomerFilter, setSiteCustomerFilter] = useState('all');
  const [auditorFilter, setAuditorFilter] = useState('');
  const [regionId, setRegionId] = useState(null);

  // Parse combined filter into separate values for API
  const customerFilter = siteCustomerFilter?.startsWith('customer:') ? siteCustomerFilter.split(':')[1] : '';
  const siteFilter = siteCustomerFilter?.startsWith('site:') ? siteCustomerFilter.split(':')[1] : '';

  // Drill-down state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [drilldownDescription, setDrilldownDescription] = useState('');
  const [drilldownJobs, setDrilldownJobs] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // iter493 — Reports index search + collapsible category state.
  const [reportSearch, setReportSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const toggleCategory = (id) => setCollapsedCategories(c => ({ ...c, [id]: !c[id] }));

  // Draft-jobs toggle — admin/scheduler only, applies to Schedule Matrix and Revenue Forecast
  const [includeDraftJobs, setIncludeDraftJobs] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const showDraftToggle = (isAdmin || isScheduler) && (selectedReport === 'schedule' || selectedReport === 'forecast');

  // Determine if user is auditor-only (has auditor role but not admin or scheduler)
  const isAuditorOnly = isAuditor && !isAdmin && !isScheduler;

  // ═══════════════════════════════════════════════════════
  //  REPORT TYPE DEFINITIONS
  // ═══════════════════════════════════════════════════════

  // iter493 — Categories drive the new grouped reports index.
  // Add a new category here (id + label + icon + accent classes) to
  // introduce a new group; then tag each report with `category: <id>`.
  const REPORT_CATEGORIES = [
    { id: 'operations', label: 'Operations & Delivery',    icon: Briefcase,     accent: 'text-sky-700 bg-sky-50 border-sky-200' },
    { id: 'financial',  label: 'Financial & Forecasting',  icon: PoundSterling, accent: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { id: 'directory',  label: 'Directory & Reference',    icon: Building2,     accent: 'text-slate-700 bg-slate-100 border-slate-200' },
  ];

  const allReportTypes = [
    {
      id: 'tracker',
      title: 'Job Tracker',
      category: 'operations',
      description: isAuditorOnly ? 'View your assigned job details and status' : 'View all jobs with KPIs, completion rates, and detailed breakdowns',
      icon: List,
      color: 'bg-blue-500',
      hoverColor: 'hover:border-blue-400',
      features: isAuditorOnly
        ? ['Your Assigned Jobs', 'Job Details', 'Status Tracking']
        : ['KPI Summary', 'Completion Metrics', 'Job Details Table', 'Export to CSV'],
      visible: true
    },
    {
      id: 'schedule',
      title: 'Schedule Matrix',
      category: 'operations',
      description: 'Visual grid showing auditor assignments by date',
      icon: Grid3X3,
      color: 'bg-indigo-500',
      hoverColor: 'hover:border-indigo-400',
      features: ['Date \u00D7 Auditor Grid', 'Weekend Highlighting', 'Bank Holiday Markers', 'Multi-day Job Support'],
      visible: true
    },
    {
      id: 'forecast',
      title: 'Revenue Forecast',
      category: 'financial',
      description: 'Financial projections by auditor and date with totals',
      icon: PoundSterling,
      color: 'bg-emerald-500',
      hoverColor: 'hover:border-emerald-400',
      features: ['Revenue by Auditor', 'Date Totals', 'Grand Total Summary', 'Allocation Settings'],
      visible: isAdmin || isScheduler
    },
    {
      id: 'preparation-quality',
      title: 'Preparation Quality',
      category: 'operations',
      description: isAuditorOnly ? 'Your first-time approval rate and report review history' : 'First-time approval rates, return history, and reviewer comments for audit reports',
      icon: Award,
      color: 'bg-violet-500',
      hoverColor: 'hover:border-violet-400',
      features: isAuditorOnly
        ? ['Your Approval Rate', 'Submission History', 'Review Comments']
        : ['First-Time Approval Rate', 'Auditor Performance', 'Rejection History', 'Reviewer Comments'],
      visible: true
    },
    {
      id: 'audit-compliance',
      title: 'Audit Compliance',
      category: 'operations',
      description: 'Identify sites overdue for audits based on frequency settings',
      icon: Calendar,
      color: 'bg-amber-500',
      hoverColor: 'hover:border-amber-400',
      features: ['Overdue Sites', 'Compliance Rate', 'Billing at Risk', 'PDF Export'],
      visible: isAdmin || isScheduler,
      navigateTo: '/reports/audit-frequency-compliance'
    },
    {
      // iter447 — Forecast Revenue Variance report
      id: 'forecast-variance',
      title: 'Forecast Revenue Variance',
      category: 'financial',
      description: 'Compare any two months of forecasted revenue by group and site, with variance drivers and audit-frequency overlay',
      icon: TrendingUp,
      color: 'bg-indigo-500',
      hoverColor: 'hover:border-indigo-400',
      features: ['Baseline vs Comparison', 'Variance Drivers', 'Group & Site Rollup', 'Frequency Overlay'],
      visible: isAdmin || isScheduler,
      navigateTo: '/reports/forecast-variance'
    },
    {
      // iter478 — Revenue Budget report (Phase 1)
      id: 'revenue-budget',
      title: 'Revenue Budget',
      category: 'financial',
      description: 'Forward-looking annual revenue plan built from customer master. Projects next-year audits from last-audit + frequency, with per-month totals, last-year comparison and variance.',
      icon: TrendingUp,
      color: 'bg-emerald-500',
      hoverColor: 'hover:border-emerald-400',
      features: ['Auto-Projection', 'Monthly Grid', 'Last-Year Variance', 'Uplifts', 'Lock Versions', 'Excel + PDF'],
      visible: isAdmin || isScheduler,
      navigateTo: '/reports/revenue-budget'
    },
    {
      id: 'customer-directory',
      title: 'Customer & Site Directory',
      category: 'directory',
      description: isAuditorOnly ? 'View your assigned sites and contacts' : 'Searchable directory of all customers, sites, contacts and operational flags',
      icon: Building2,
      color: 'bg-teal-500',
      hoverColor: 'hover:border-teal-400',
      features: isAuditorOnly
        ? ['Your Assigned Sites', 'Contact Details', 'Audit History', 'Export']
        : ['Full Site Directory', 'Operational Flags', 'Sales & Pricing', 'CSV & PDF Export'],
      visible: true,
      navigateTo: '/reports/customer-site-directory'
    },
    {
      id: 'report-completeness',
      title: 'Report Completeness Audit',
      category: 'operations',
      description: 'Assess which Summary Stock Audit Reports have each required section populated (sections and templates managed in Settings).',
      icon: ClipboardList,
      color: 'bg-violet-500',
      hoverColor: 'hover:border-violet-400',
      features: ['Section Coverage', 'Template Completion', 'Per-Report Breakdown', 'CSV Export'],
      visible: isAdmin || isScheduler,
      navigateTo: '/reports/completeness'
    },
    {
      id: 'invoice-list',
      title: 'Invoice List',
      category: 'financial',
      description: 'Generate a billable invoice list for a date range. Assigns sequential invoice numbers, calculates VAT at 20%, and exports the nine-column reference workbook with Excel formulas.',
      icon: Receipt,
      color: 'bg-emerald-600',
      hoverColor: 'hover:border-emerald-500',
      features: ['Sequential Invoice Numbers', '20% VAT Calculation', 'Cellar Ledger Flag', 'Excel Export with Formulas'],
      visible: (isAdmin || isSeniorScheduler) && !salesInvoicingStatusLoading && salesInvoicingEnabled,  // 2026-11: hidden while Sales Invoicing is paused (and while status is still loading)
      navigateTo: '/reports/invoice-list'
    }
  ];

  const reportTypes = allReportTypes.filter(r => r.visible);

  // ═══════════════════════════════════════════════════════
  //  DATA LOADING
  // ═══════════════════════════════════════════════════════

  // Load reference data - filtered by region
  useEffect(() => {
    const loadRefData = async () => {
      try {
        const [customersData, sitesData, auditorsData] = await Promise.all([
          clientsService.list({ is_active: true, region }).catch(() => []),
          sitesService.list({ is_active: true, region }).catch(() => []),
          auditorsService.list({ is_active: true }).catch(() => [])
        ]);
        setCustomers(customersData);
        setSites(sitesData);
        setAuditors(
          [...auditorsData].sort((a, b) =>
            (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '')
          )
        );
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    loadRefData();
  }, [region]);

  // Load bank holidays when region changes
  useEffect(() => {
    const loadBankHolidays = async () => {
      try {
        const holidays = await settingsService.getBankHolidays({ region });
        if (holidays && holidays.length > 0) {
          setBankHolidays(holidays.map(h => ({ date: h.date, name: h.name })));
        }
      } catch (e) {
        console.error('Failed to load bank holidays:', e);
        setBankHolidays([]);
      }
    };
    loadBankHolidays();
  }, [region]);

  // Load KPIs and jobs - filtered by region
  const loadData = useCallback(async (reportType = null) => {
    setLoading(true);
    try {
      const currentReport = reportType || selectedReport;

      // Preparation Quality has its own endpoint — load separately
      if (currentReport === 'preparation-quality') {
        setQualityLoading(true);
        try {
          const qualityResult = await reportsService.getPreparationQuality({
            start_date: startDate,
            end_date: endDate,
            client_id: customerFilter || undefined,
            site_id: siteFilter || undefined,
            auditor_id: auditorFilter || undefined,
            region: region || undefined,
            region_id: regionId != null ? regionId : undefined,
          });
          setQualityData(qualityResult);
        } catch (err) {
          toast({ title: 'Error', description: 'Failed to load preparation quality data', variant: 'destructive' });
        } finally {
          setQualityLoading(false);
          setLoading(false);
        }
        return;
      }

      const useForReports = currentReport === 'schedule' || currentReport === 'forecast';

      const params = {
        start_date: startDate,
        end_date: endDate,
        client_id: customerFilter || undefined,
        site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined,
        region: region || undefined,
        region_id: regionId != null ? regionId : undefined,
        limit: 5000,
        for_reports: useForReports,
        include_draft_jobs: useForReports ? includeDraftJobs : undefined
      };

      const jobsResult = await jobsService.list(params);
      setJobs(jobsResult);

      if (currentReport === 'tracker' && isAuditorOnly) {
        const clientJobs = jobsResult.filter(j => !j.event_type?.startsWith('LEAVE'));
        const today = new Date();

        const completedJobs = clientJobs.filter(j => j.status === 'completed');
        const scheduledJobs = clientJobs.filter(j => j.status === 'scheduled').length;
        const cancelledJobs = clientJobs.filter(j => j.status === 'cancelled').length;
        const overdueJobs = clientJobs.filter(j =>
          j.status === 'scheduled' &&
          new Date(j.scheduled_date) < today
        ).length;

        let avgDaysToComplete = null;
        let pctWithin1Day = 0;

        const completedWithDates = completedJobs.filter(j => j.completed_on || j.completed_at);
        if (completedWithDates.length > 0) {
          let daysList = [];
          let within1DayCount = 0;

          completedWithDates.forEach(job => {
            const days = job.days_to_complete;
            if (days !== null && days !== undefined) {
              daysList.push(days);
              if (days <= 1 || job.delay_justified) {
                within1DayCount++;
              }
            }
          });

          if (daysList.length > 0) {
            avgDaysToComplete = Math.round((daysList.reduce((a, b) => a + b, 0) / daysList.length) * 100) / 100;
            pctWithin1Day = Math.round((within1DayCount / daysList.length) * 1000) / 10;
          }
        }

        setKpiData({
          total_jobs: clientJobs.length,
          completed_jobs: completedJobs.length,
          scheduled_jobs: scheduledJobs,
          cancelled_jobs: cancelledJobs,
          overdue_jobs: overdueJobs,
          avg_days_to_complete: avgDaysToComplete,
          pct_completed_within_1_day: pctWithin1Day
        });
      } else {
        const kpiResult = await reportsService.getKPI(params);
        setKpiData(kpiResult);
      }

    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load report data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, customerFilter, siteFilter, auditorFilter, region, regionId, toast, selectedReport, isAuditorOnly, includeDraftJobs]);

  useEffect(() => {
    if (selectedReport) {
      setTrackerPage(0);
      loadData(selectedReport);
    }
  }, [selectedReport, startDate, endDate, customerFilter, siteFilter, auditorFilter, region, regionId]);

  // ═══════════════════════════════════════════════════════
  //  DRILL-DOWN HANDLERS
  // ═══════════════════════════════════════════════════════

  const handleDrilldown = async (filterFn, title, description) => {
    setDrilldownTitle(title);
    setDrilldownDescription(description);
    setDrilldownOpen(true);
    setDrilldownLoading(true);

    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        client_id: customerFilter || undefined,
        site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined,
        region: region || undefined,
        limit: 5000
      };

      const allJobs = await jobsService.list(params);
      const clientJobs = allJobs.filter(j => !j.event_type || j.event_type === 'CLIENT_JOB');
      setDrilldownJobs(clientJobs.filter(filterFn));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load drill-down data', variant: 'destructive' });
    } finally {
      setDrilldownLoading(false);
    }
  };

  const drilldownTotalJobs = () => handleDrilldown(
    () => true,
    'Total Jobs',
    `All jobs between ${format(parseISO(startDate), 'd MMM yyyy')} and ${format(parseISO(endDate), 'd MMM yyyy')}`
  );

  const drilldownCompleted = () => handleDrilldown(
    (job) => job.status === 'completed',
    'Completed Jobs',
    'Jobs that have been marked as completed'
  );

  const drilldownScheduled = () => handleDrilldown(
    (job) => job.status === 'scheduled',
    'Scheduled Jobs',
    'Jobs that are still scheduled/pending'
  );

  const drilldownCancelled = () => handleDrilldown(
    (job) => job.status === 'cancelled',
    'Cancelled Jobs',
    'Jobs that were cancelled'
  );

  const drilldownWithin1Day = () => handleDrilldown(
    (job) => job.status === 'completed' && job.days_to_complete !== null && job.days_to_complete <= 1,
    'Completed Within 1 Day',
    'Jobs completed on or within 1 day of the scheduled date'
  );

  // ═══════════════════════════════════════════════════════
  //  EXPORT HANDLERS (CSV)
  // ═══════════════════════════════════════════════════════

  const exportJobTracker = () => {
    const clientJobs = jobs.filter(job => !job.event_type?.startsWith('LEAVE'));
    const headers = ['Job ID', 'Start Date', 'End Date', 'Completion Date', 'Days to Complete', 'Status', 'Customer', 'Site', 'Service', 'Auditors'];
    const data = clientJobs.map(job => {
      const completionDate = job.completed_on || job.completed_at;
      return [
        job.id,
        job.scheduled_date,
        job.end_date || job.scheduled_date,
        job.status === 'completed' && completionDate ? format(parseISO(completionDate), 'yyyy-MM-dd') : '',
        job.days_to_complete ?? '',
        job.status,
        getJobCustomer(job)?.name || '',
        job.site?.name || '',
        job.service?.name || '',
        job.auditors?.slice().sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '')).map(a => `${a.first_name} ${a.last_name}`).join('; ') || ''
      ];
    });
    exportToCSV(data, `job_tracker_${startDate}_to_${endDate}.csv`, headers);
    toast({ title: 'Export Complete', description: 'Job Tracker exported to CSV' });
  };

  const exportSchedule = () => {
    const dates = getDateRange(startDate, endDate);
    const headers = ['Date', 'Day', ...auditors.map(a => `${a.first_name} ${a.last_name}`)];
    const data = dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const holiday = getBankHoliday(bankHolidays, dateStr);
      const row = [
        format(date, 'yyyy-MM-dd'),
        format(date, 'EEE') + (holiday ? ` (${holiday.name})` : '')
      ];
      auditors.forEach(auditor => {
        const dayJobs = getJobsForAuditorDate(jobs, auditor.id, dateStr);
        row.push(dayJobs.map(j => getJobLabel(j)).join('; '));
      });
      return row;
    });
    exportToCSV(data, `schedule_${startDate}_to_${endDate}.csv`, headers);
    toast({ title: 'Export Complete', description: 'Schedule exported to CSV' });
  };

  // Forecast allocation calculation (for CSV export only; render version lives in RevenueForecastReport)
  const calculateForecastAllocations = () => {
    const dates = getDateRange(startDate, endDate);
    const auditorTotals = {};
    const dateTotals = {};
    let grandTotal = 0;

    auditors.forEach(auditor => { auditorTotals[auditor.id] = 0; });
    dates.forEach(date => { dateTotals[format(date, 'yyyy-MM-dd')] = 0; });

    const jobAllocations = new Map();

    jobs.forEach(job => {
      const jobId = job.id;
      if (job.event_type?.startsWith('LEAVE')) return;

      const price = job.effective_forecast || job.site?.forecast_price || 0;
      if (price === 0) return;

      const jobAuditors = job.auditors || [];
      const hasAssignments = job.assignments && job.assignments.length > 0;
      const activeAuditorIds = new Set(jobAuditors.map(a => a.id));
      if (activeAuditorIds.size === 0) return;

      let totalAuditorDays = 0;
      if (hasAssignments) {
        job.assignments.filter(a => activeAuditorIds.has(a.auditor_id)).forEach(assignment => {
          const s = parseISO(assignment.start_date);
          const e = parseISO(assignment.end_date);
          totalAuditorDays += Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
        });
        if (totalAuditorDays === 0) totalAuditorDays = getJobSpan(job) * activeAuditorIds.size;
      } else {
        totalAuditorDays = getJobSpan(job) * activeAuditorIds.size;
      }

      activeAuditorIds.forEach(auditorId => {
        const auditor = jobAuditors.find(a => a.id === auditorId) || { id: auditorId };
        let auditorDates = [];
        let auditorDays = 0;

        if (hasAssignments) {
          const auditorAssignments = job.assignments.filter(a => a.auditor_id === auditorId);
          if (auditorAssignments.length > 0) {
            auditorAssignments.forEach(assignment => {
              dates.forEach(date => {
                const ds = format(date, 'yyyy-MM-dd');
                if (ds >= assignment.start_date && ds <= assignment.end_date) {
                  if (!auditorDates.includes(ds)) auditorDates.push(ds);
                }
              });
              const s = parseISO(assignment.start_date);
              const e = parseISO(assignment.end_date);
              auditorDays += Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
            });
            auditorDates.sort();
            // Stale-assignment guard: assignments exist but ALL dates fall outside the
            // reporting period (e.g. job rescheduled after batch creation).
            // Fall back to job-level dates so revenue is never silently dropped.
            if (auditorDates.length === 0) {
              const jobStart = job.scheduled_date;
              const jobEnd = job.end_date || job.scheduled_date;
              dates.forEach(date => {
                const ds = format(date, 'yyyy-MM-dd');
                if (ds >= jobStart && ds <= jobEnd) if (!auditorDates.includes(ds)) auditorDates.push(ds);
              });
              if (auditorDays === 0) auditorDays = getJobSpan(job);
            }
          }
        } else if (job.job_dates && job.job_dates.length > 0) {
          auditorDates = job.job_dates.filter(d => dates.some(date => format(date, 'yyyy-MM-dd') === d)).sort();
          auditorDays = job.job_dates.length;
        } else {
          const jobStart = job.scheduled_date;
          const jobEnd = job.end_date || job.scheduled_date;
          dates.forEach(date => {
            const ds = format(date, 'yyyy-MM-dd');
            if (ds >= jobStart && ds <= jobEnd) auditorDates.push(ds);
          });
          auditorDays = getJobSpan(job);
        }

        if (auditorDates.length === 0) return;
        const auditorShare = (price * auditorDays) / totalAuditorDays;

        if (forecastAllocationDays === 'first_day') {
          auditorDates.forEach((dateStr, idx) => {
            const isFirstDay = idx === 0;
            if (!jobAllocations.has(jobId)) jobAllocations.set(jobId, []);
            jobAllocations.get(jobId).push({ auditorId: auditor.id, dateStr, amount: isFirstDay ? auditorShare : 0, isFirst: isFirstDay });
            if (isFirstDay) {
              auditorTotals[auditor.id] = Math.round((auditorTotals[auditor.id] + auditorShare) * 100) / 100;
              dateTotals[dateStr] = Math.round((dateTotals[dateStr] + auditorShare) * 100) / 100;
              grandTotal = Math.round((grandTotal + auditorShare) * 100) / 100;
            }
          });
        } else {
          if (auditorDates.length === 0) return;
          const perDay = auditorShare / auditorDates.length;
          let remaining = auditorShare;
          auditorDates.forEach((dateStr, idx) => {
            const isLast = idx === auditorDates.length - 1;
            const dayAmount = isLast ? remaining : Math.round(perDay * 100) / 100;
            remaining = Math.round((remaining - dayAmount) * 100) / 100;
            if (!jobAllocations.has(jobId)) jobAllocations.set(jobId, []);
            jobAllocations.get(jobId).push({ auditorId: auditor.id, dateStr, amount: dayAmount, isFirst: idx === 0 });
            auditorTotals[auditor.id] = Math.round((auditorTotals[auditor.id] + dayAmount) * 100) / 100;
            dateTotals[dateStr] = Math.round((dateTotals[dateStr] + dayAmount) * 100) / 100;
            grandTotal = Math.round((grandTotal + dayAmount) * 100) / 100;
          });
        }
      });
    });

    return { dates, jobAllocations, auditorTotals, dateTotals, grandTotal };
  };

  const exportForecast = () => {
    const { dates, jobAllocations, auditorTotals, dateTotals, grandTotal } = calculateForecastAllocations();
    const headers = ['Date', 'Day', ...auditors.map(a => `${a.first_name} ${a.last_name}`), 'Total'];

    const getCellAllocations = (auditorId, dateStr) => {
      const result = [];
      jobAllocations.forEach((allocations, jobId) => {
        allocations.forEach(alloc => {
          if (alloc.auditorId === auditorId && alloc.dateStr === dateStr) {
            const job = jobs.find(j => j.id === jobId);
            result.push({ job, amount: alloc.amount, isFirst: alloc.isFirst });
          }
        });
      });
      return result;
    };

    const data = dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const holiday = getBankHoliday(bankHolidays, dateStr);
      const row = [
        format(date, 'yyyy-MM-dd'),
        format(date, 'EEE') + (holiday ? ` (${holiday.name})` : '')
      ];

      auditors.forEach(auditor => {
        const cellAllocations = getCellAllocations(auditor.id, dateStr);
        const cellContent = cellAllocations.map(({ job, amount }) => {
          const amountStr = amount > 0 ? `: ${currencySymbol}${amount.toFixed(2)}` : '';
          return `${getJobLabel(job)}${amountStr}`;
        });
        row.push(cellContent.join('; '));
      });

      const dateTotal = dateTotals[dateStr] || 0;
      row.push(dateTotal > 0 ? `${currencySymbol}${dateTotal.toFixed(2)}` : '');
      return row;
    });

    const totalsRow = ['TOTAL', ''];
    auditors.forEach(auditor => {
      const total = auditorTotals[auditor.id] || 0;
      totalsRow.push(total > 0 ? `${currencySymbol}${total.toFixed(2)}` : '');
    });
    totalsRow.push(`${currencySymbol}${grandTotal.toFixed(2)}`);
    data.push(totalsRow);

    exportToCSV(data, `forecast_${startDate}_to_${endDate}.csv`, headers);
    toast({ title: 'Export Complete', description: 'Forecast exported to CSV' });
  };

  // ═══════════════════════════════════════════════════════
  //  PDF EXPORT & PRINT HANDLERS
  // ═══════════════════════════════════════════════════════

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      const params = {
        start_date: startDate, end_date: endDate,
        client_id: customerFilter || undefined, site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined, region: region || undefined,
        region_id: regionId != null ? regionId : undefined
      };

      let blob;
      let filename;

      if (selectedReport === 'tracker') {
        blob = await reportsService.exportJobsTrackerPDF(params);
        filename = `job_tracker_${startDate}_to_${endDate}.pdf`;
      } else if (selectedReport === 'schedule') {
        blob = await reportsService.exportScheduleMatrixPDF(params);
        filename = `schedule_matrix_${startDate}_to_${endDate}.pdf`;
      } else if (selectedReport === 'forecast') {
        blob = await reportsService.exportRevenueForecastPDF({ ...params, allocation_days: forecastAllocationDays });
        filename = `revenue_forecast_${startDate}_to_${endDate}.pdf`;
      }

      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast({ title: 'Export Complete', description: `${filename} downloaded` });
      }
    } catch (err) {
      console.error('PDF export error:', err);
      toast({
        title: 'Export Failed',
        description: err.response?.status === 403 ? 'Access denied. Admin only.' : 'Failed to generate PDF',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    setPrintBlocked(false);
    try {
      setLoading(true);
      const params = {
        start_date: startDate, end_date: endDate,
        client_id: customerFilter || undefined, site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined, region: region || undefined,
        region_id: regionId != null ? regionId : undefined
      };

      let blob;

      if (selectedReport === 'tracker') {
        blob = await reportsService.exportJobsTrackerPDF(params);
      } else if (selectedReport === 'schedule') {
        blob = await reportsService.exportScheduleMatrixPDF(params);
      } else if (selectedReport === 'forecast') {
        blob = await reportsService.exportRevenueForecastPDF({ ...params, allocation_days: forecastAllocationDays });
      }

      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => { printWindow.print(); };
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `report_${startDate}_to_${endDate}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast({ title: 'PDF Downloaded', description: 'Popup blocked - PDF downloaded instead. Open and print manually.' });
        }
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Print error:', e);
      setPrintBlocked(true);
      toast({
        title: 'Print Failed',
        description: e.response?.status === 403 ? 'Access denied. Admin only.' : 'Failed to generate PDF for printing.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════
  //  MISC HELPERS
  // ═══════════════════════════════════════════════════════

  const resetFilters = () => {
    setStartDate(format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
    setSiteCustomerFilter('all');
    setAuditorFilter('');
    setRegionId(null);
    setIncludeDraftJobs(false);
  };

  const getReportTitle = () => {
    switch (selectedReport) {
      case 'tracker': return 'Job Tracker';
      case 'schedule': return 'Schedule Matrix';
      case 'forecast': return 'Revenue Forecast';
      default: return 'Report';
    }
  };

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Toaster />

      {/* Print Header - Hidden on screen, visible on print */}
      <div className="hidden print:block print-report-header" style={{ marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #334155' }}>
        <h1 style={{ fontSize: '16pt', fontWeight: '700', margin: '0 0 4px 0', color: '#1e293b' }}>
          Abbey 5 Star Ltd - {getReportTitle()}
        </h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10pt', color: '#475569' }}>
          <span>
            Period: {format(parseISO(startDate), 'd MMM yyyy')} - {format(parseISO(endDate), 'd MMM yyyy')}
            {' | '}
            Region: {region?.toUpperCase() || 'ALL'}
          </span>
          <span>
            Generated: {format(new Date(), 'd MMM yyyy \'at\' HH:mm')}
          </span>
        </div>
      </div>

      {/* Header with back button when report is selected */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          {selectedReport && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedReport(null)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-to-reports-btn"
            >
              <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
              Back
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold" data-testid="reports-title">
              {selectedReport ? reportTypes.find(r => r.id === selectedReport)?.title : 'Reports'}
            </h1>
            <p className="text-muted-foreground">
              {selectedReport
                ? reportTypes.find(r => r.id === selectedReport)?.description
                : 'Select a report to view analytics, schedules and forecasts'}
            </p>
          </div>
        </div>
        {selectedReport && (
          <div className="flex gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  if (selectedReport === 'tracker') exportJobTracker();
                  else if (selectedReport === 'schedule') exportSchedule();
                  else if (selectedReport === 'forecast') exportForecast();
                }}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExportPDF()}>
                  <Printer className="h-4 w-4 mr-2" />
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Print blocked warning */}
      {printBlocked && selectedReport && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 print:hidden">
          Print blocked by browser settings. Please allow popups or try using Ctrl/Cmd + P.
        </div>
      )}

      {/* Report Selection - Show when no report is selected */}
      {!selectedReport && (
        <div className="space-y-4 print:hidden" data-testid="reports-index">

          {/* Performance Centre featured entry */}
          {(isAdmin || isScheduler) && (
            <button
              onClick={() => navigate('/performance')}
              className="w-full text-left rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 flex items-center gap-4 hover:border-emerald-400 hover:shadow-sm transition-all group"
              data-testid="performance-centre-featured-card"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-slate-800">Performance Centre</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">New</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Revenue trends, operational backlog, site compliance and FTA quality — all in one view.
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-emerald-400 group-hover:text-emerald-600 shrink-0 transition-colors" />
            </button>
          )}

          {/* iter493 — Search bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={reportSearch}
              onChange={(e) => setReportSearch(e.target.value)}
              placeholder="Search reports by name, description or feature…"
              className="pl-9"
              data-testid="reports-search-input"
            />
          </div>

          {/* iter493 — Categorised, compact list. Each category is a
              collapsible section with a header row (category label +
              count + expand/collapse chevron) and a stack of report
              rows underneath. Rows are far tighter than the old
              hero tiles so >20 reports still fit on one screen. */}
          {(() => {
            const q = reportSearch.trim().toLowerCase();
            const match = (r) => !q
              || r.title.toLowerCase().includes(q)
              || (r.description || '').toLowerCase().includes(q)
              || (r.features || []).some(f => f.toLowerCase().includes(q));
            const visibleTypes = reportTypes.filter(match);
            const bucketFor = (r) => REPORT_CATEGORIES.find(c => c.id === r.category)?.id || 'other';
            const grouped = REPORT_CATEGORIES.map(cat => ({
              ...cat,
              reports: visibleTypes.filter(r => bucketFor(r) === cat.id),
            })).filter(cat => cat.reports.length > 0);
            const uncategorised = visibleTypes.filter(r => !REPORT_CATEGORIES.some(c => c.id === r.category));
            if (uncategorised.length > 0) {
              grouped.push({ id: 'other', label: 'Other', icon: BarChart3, accent: 'text-slate-700 bg-slate-100 border-slate-200', reports: uncategorised });
            }

            if (grouped.length === 0) {
              return (
                <Card>
                  <CardContent className="py-10 text-center text-sm text-muted-foreground">
                    No reports match &ldquo;{reportSearch}&rdquo;. Clear the search to see all.
                  </CardContent>
                </Card>
              );
            }

            return grouped.map(cat => {
              const CatIcon = cat.icon;
              const collapsed = !!collapsedCategories[cat.id] && !q;
              return (
                <div key={cat.id} className="rounded-lg border bg-white" data-testid={`reports-category-${cat.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 border-b ${cat.accent}`}
                    data-testid={`reports-category-toggle-${cat.id}`}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    <CatIcon className="h-4 w-4" />
                    <span className="font-semibold text-sm">{cat.label}</span>
                    <span className="text-xs opacity-70">({cat.reports.length})</span>
                  </button>
                  {!collapsed && (
                    <ul className="divide-y">
                      {cat.reports.map(report => {
                        const Icon = report.icon;
                        return (
                          <li key={report.id}>
                            <button
                              type="button"
                              onClick={() => {
                                if (report.navigateTo) navigate(report.navigateTo);
                                else setSelectedReport(report.id);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors group"
                              data-testid={`report-row-${report.id}`}
                            >
                              <div className={`${report.color} w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0`}>
                                <Icon className="h-5 w-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{report.title}</span>
                                  {(report.features || []).slice(0, 3).map(f => (
                                    <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap">
                                      {f}
                                    </span>
                                  ))}
                                  {(report.features || []).length > 3 && (
                                    <span className="text-[10px] text-slate-400">+{report.features.length - 3} more</span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                  {report.description}
                                </p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            });
          })()}

          {/* Quick Info */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <BarChart3 className="h-5 w-5 text-slate-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800 text-sm">Report Categories</p>
                  <p className="text-xs text-slate-600">
                    Reports are grouped by purpose. Click a category header to collapse it, or use search to jump straight to a report. All reports respect the current region setting ({region}).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Show filters and report content when a report is selected */}
      {selectedReport && (
        <>
          {/* Print hint for Preview mode */}
          <div className="text-xs text-muted-foreground print:hidden">
            If Print doesn't open in Preview, open the app in a new browser tab/window.
          </div>

          {/* Filters Card */}
          <Card className="print:hidden">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Filters
                </CardTitle>
                {/* Quick Date Range Presets */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const now = new Date();
                      setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
                      setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                    }}
                    data-testid="preset-this-month"
                    className="text-xs"
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lastMonth = subMonths(new Date(), 1);
                      setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
                      setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
                    }}
                    data-testid="preset-last-month"
                    className="text-xs"
                  >
                    Last Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const now = new Date();
                      setStartDate(format(startOfQuarter(now), 'yyyy-MM-dd'));
                      setEndDate(format(endOfQuarter(now), 'yyyy-MM-dd'));
                    }}
                    data-testid="preset-this-quarter"
                    className="text-xs"
                  >
                    This Quarter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      setStartDate(newStart);
                      if (endDate && newStart > endDate) {
                        setEndDate(newStart);
                      }
                    }}
                    data-testid="start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        const newEnd = e.target.value;
                        if (startDate && newEnd < startDate) {
                          toast({ title: 'Invalid Date', description: 'End date cannot be before start date', variant: 'destructive' });
                          return;
                        }
                        setEndDate(newEnd);
                      }}
                      min={startDate}
                      data-testid="end-date"
                    />
                    {/* Quick Date Range Buttons */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" data-testid="quick-date-range-btn">
                          <CalendarRange className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          const now = new Date();
                          setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
                          setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                        }}>
                          This Month
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          const lastMonth = subMonths(new Date(), 1);
                          setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
                          setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
                        }}>
                          Last Month
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          const nextMonth = addMonths(new Date(), 1);
                          setStartDate(format(startOfMonth(nextMonth), 'yyyy-MM-dd'));
                          setEndDate(format(endOfMonth(nextMonth), 'yyyy-MM-dd'));
                        }}>
                          Next Month
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => {
                          const now = new Date();
                          setStartDate(format(startOfQuarter(now), 'yyyy-MM-dd'));
                          setEndDate(format(endOfQuarter(now), 'yyyy-MM-dd'));
                        }}>
                          This Quarter
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Show advanced filters based on role and report type */}
                {(!isAuditorOnly || selectedReport === 'schedule') && (
                  <>
                    {!isAuditorOnly && (
                      <div className="space-y-2">
                        <Label>Customer / Site</Label>
                        <SiteCustomerCombobox
                          clients={customers}
                          sites={sites}
                          value={siteCustomerFilter}
                          onValueChange={setSiteCustomerFilter}
                          placeholder="All Customers & Sites"
                          className="w-full"
                          data-testid="reports-site-customer-filter"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Auditor</Label>
                      <Select value={auditorFilter || "all"} onValueChange={(v) => setAuditorFilter(v === "all" ? "" : v)}>
                        <SelectTrigger data-testid="auditor-filter">
                          <SelectValue placeholder="All Auditors" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Auditors</SelectItem>
                          {auditors.map((auditor) => (
                            <SelectItem key={auditor.id} value={auditor.id.toString()}>
                              {auditor.first_name} {auditor.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {/* Region Filter */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Region</label>
                  <RegionFilter value={regionId} onChange={setRegionId} />
                </div>

                {/* Include Draft Jobs — admin/scheduler only, schedule and forecast reports */}
                {showDraftToggle && (
                  <div className="flex flex-col gap-1.5 justify-end">
                    <label className="text-sm font-medium text-transparent select-none" aria-hidden="true">.</label>
                    <button
                      type="button"
                      data-testid="include-draft-jobs-toggle"
                      onClick={() => setIncludeDraftJobs(v => !v)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                        includeDraftJobs
                          ? 'bg-amber-50 border-amber-400 text-amber-800'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                        includeDraftJobs ? 'bg-amber-500 border-amber-500' : 'border-gray-300'
                      }`}>
                        {includeDraftJobs && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                      Include draft jobs
                    </button>
                  </div>
                )}

                {/* Unpublish Jobs — Schedule Matrix toolbar, scheduler+ only */}
                {(isAdmin || isScheduler) && selectedReport === 'schedule' && (
                  <div className="flex flex-col gap-1.5 justify-end">
                    <label className="text-sm font-medium text-transparent select-none" aria-hidden="true">.</label>
                    <button
                      type="button"
                      data-testid="matrix-unpublish-btn"
                      onClick={() => setUnpublishDialogOpen(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                    >
                      <EyeOff className="w-4 h-4" />
                      Unpublish
                    </button>
                  </div>
                )}

                <div className="flex items-end">
                  <Button variant="ghost" onClick={resetFilters} className="w-full">
                    Reset
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Content */}
          <div className="space-y-6">
            {selectedReport === 'tracker' && (
              <JobTrackerReport
                jobs={jobs}
                loading={loading}
                kpiData={kpiData}
                trackerPage={trackerPage}
                setTrackerPage={setTrackerPage}
                trackerPageSize={trackerPageSize}
                reportTitle={getReportTitle()}
                startDate={startDate}
                endDate={endDate}
                region={region}
                onDrilldownTotalJobs={drilldownTotalJobs}
                onDrilldownCompleted={drilldownCompleted}
                onDrilldownScheduled={drilldownScheduled}
                onDrilldownCancelled={drilldownCancelled}
                onDrilldownWithin1Day={drilldownWithin1Day}
              />
            )}
            {selectedReport === 'schedule' && (
              <>
                {includeDraftJobs && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800" data-testid="schedule-draft-jobs-banner">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    DRAFT JOBS INCLUDED — this view includes jobs from draft, pending-approval and returned batches. Data may change before batch approval.
                  </div>
                )}
                <ScheduleMatrixReport
                  jobs={jobs}
                  loading={loading}
                  auditors={auditors}
                  startDate={startDate}
                  endDate={endDate}
                  bankHolidays={bankHolidays}
                  reportTitle={getReportTitle()}
                  region={region}
                />
              </>
            )}
            {selectedReport === 'forecast' && (
              <>
                {includeDraftJobs && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800" data-testid="forecast-draft-jobs-banner">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    DRAFT JOBS INCLUDED — this view includes jobs from draft, pending-approval and returned batches. Data may change before batch approval.
                  </div>
                )}
                <RevenueForecastReport
                  jobs={jobs}
                  loading={loading}
                  auditors={auditors}
                  startDate={startDate}
                  endDate={endDate}
                  bankHolidays={bankHolidays}
                  forecastAllocationDays={forecastAllocationDays}
                  forecastAllocationAuditors={forecastAllocationAuditors}
                  formatCurrency={formatCurrency}
                  reportTitle={getReportTitle()}
                  region={region}
                />
              </>
            )}
            {selectedReport === 'preparation-quality' && (
              <PreparationQualityReport
                data={qualityData}
                loading={qualityLoading}
                reportTitle={getReportTitle()}
                startDate={startDate}
                endDate={endDate}
                region={region}
              />
            )}
          </div>
        </>
      )}

      {/* Drill-down Dialog */}
      <DrilldownDialog
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
        title={drilldownTitle}
        description={drilldownDescription}
        jobs={drilldownJobs}
        loading={drilldownLoading}
      />

      {/* Print Styles - Component-specific overrides */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm 12mm 15mm 12mm;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print\\:hidden,
          [data-testid="back-to-reports-btn"],
          [data-testid="print-btn"],
          [data-testid="export-btn"],
          nav,
          .sidebar {
            display: none !important;
          }

          .print\\:block {
            display: block !important;
          }

          .print-report-header {
            display: block !important;
          }

          .print-section table {
            page-break-inside: auto;
          }

          .print-section thead {
            display: table-header-group;
          }

          .print-section tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .bg-slate-100, .bg-slate-200, .bg-slate-50,
          .bg-amber-50, .bg-amber-100,
          .bg-green-100, .bg-green-50,
          .bg-blue-100, .bg-red-100, .bg-purple-100 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .job-cell {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .totals-row {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          [data-slot="card"],
          .card {
            border: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* Unpublish Jobs Dialog — Schedule Matrix (Admin / Senior Scheduler / Scheduler) */}
      {(isAdmin || isScheduler) && (
        <UnpublishJobsDialog
          open={unpublishDialogOpen}
          onOpenChange={setUnpublishDialogOpen}
          month={startDate ? parseInt(startDate.split('-')[1]) : new Date().getMonth() + 1}
          year={startDate ? parseInt(startDate.split('-')[0]) : new Date().getFullYear()}
          jobs={jobs}
          sites={sites}
          auditors={auditors}
          clients={customers}
          onSuccess={(result) => {
            // Refresh report data after unpublish
            if (typeof loadData === 'function') loadData();
          }}
        />
      )}
    </div>
  );
}
